import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Avatar,
  Chip,
  TextField,
  Alert,
  Divider,
  useMediaQuery,
  IconButton,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  LinearProgress
} from '@mui/material';
import {
  ArrowBack,
  AccessTime,
  Security,
  Chat,
  Upload,
  CheckCircle,
  Warning,
  Info
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import P2PMarketplaceService from '../../services/p2pMarketplace.service';
import P2PChatDialog from './P2PChatDialog';

const P2PTransactionDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDarkMode = theme.palette.mode === 'dark';
  const fundingBal = useSelector((state) => state.wallet.fundingBalance);

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentProof, setPaymentProof] = useState('');
  const [paymentFile, setPaymentFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [chatOpen, setChatOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [lastStatus, setLastStatus] = useState(null);
  const [releasingCoins, setReleasingCoins] = useState(false);
  const [statusUpdated, setStatusUpdated] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [isPolling, setIsPolling] = useState(false);

  // Initial data fetch
  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  // Set up timers only after initial load is complete
  useEffect(() => {
    if (!initialLoadComplete || !order) return;

    // Timer for countdown
    const countdownTimer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    
    // Timer for polling order status (every 5 seconds) - less frequent to avoid conflicts
    const statusTimer = setInterval(() => {
      if (orderId && order && !['completed', 'cancelled', 'disputed'].includes(order.status) && !isPolling) {
        setIsPolling(true);
        fetchOrderDetails(true).finally(() => setIsPolling(false)); // Pass true for polling to avoid loading state
      }
    }, 5000);
    
    // Timer for polling chat messages (every 10 seconds) - less frequent
    const chatTimer = setInterval(() => {
      if (orderId) {
        fetchChatMessages();
      }
    }, 10000);
    
    return () => {
      clearInterval(countdownTimer);
      clearInterval(statusTimer);
      clearInterval(chatTimer);
    };
  }, [orderId, order, initialLoadComplete, isPolling]);

  const fetchOrderDetails = async (isPolling = false) => {
    if (!isPolling) {
      setLoading(true);
    }
    try {
      const response = await P2PMarketplaceService.getOrderDetails(orderId);
      if (response.success) {
        const orderData = response.order;
        
        // Calculate time left if order has expiration
        let timeLeftSeconds = 0;
        if (orderData.expires_at) {
          const expiresAt = new Date(orderData.expires_at);
          const now = new Date();
          timeLeftSeconds = Math.max(0, Math.floor((expiresAt - now) / 1000));
        }
        
        // Check if status has changed
        if (lastStatus && lastStatus !== orderData.status) {
          setStatusUpdated(true);
          // Hide the notification after 3 seconds
          setTimeout(() => setStatusUpdated(false), 3000);
        }
        
        setOrder(orderData);
        setLastStatus(orderData.status);
        setTimeLeft(timeLeftSeconds);
        
        // Only fetch chat messages on initial load, not during polling
        if (!isPolling) {
          try {
            const chatResponse = await P2PMarketplaceService.getChatMessages(orderId);
            if (chatResponse.success) {
              setChatMessages(chatResponse.messages || []);
            }
          } catch (chatError) {
            console.error('Error fetching chat messages:', chatError);
          }
        }
      } else {
        console.error('Failed to fetch order details:', response.message);
        // Navigate back if order not found
        navigate('/p2p/history');
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      // Navigate back on error
      navigate('/p2p/history');
    } finally {
      setLoading(false);
      // Mark initial load as complete
      if (!isPolling) {
        setInitialLoadComplete(true);
      }
    }
  };

  // When order completes, refresh unified user data so /assets updates
  useEffect(() => {
    if (!order) return;
    if (lastStatus !== 'completed' && order.status === 'completed') {
      try {
        if (typeof window !== 'undefined' && window.refreshUserData) {
          window.refreshUserData();
        }
      } catch {}
    }
    setLastStatus(order.status);
  }, [order]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getOrderSteps = () => {
    const steps = [
      { label: 'Order Created', completed: true },
      { label: 'Payment Required', completed: order?.status !== 'payment_pending' },
      { label: 'Payment Confirmed', completed: ['completed'].includes(order?.status) },
      { label: 'Crypto Released', completed: order?.status === 'completed' }
    ];
    return steps;
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      // Check file type (images and PDFs only)
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        alert('Only JPEG, PNG, and PDF files are allowed');
        return;
      }
      setPaymentFile(file);
    }
  };

  const handleUploadPaymentProof = async () => {
    if (!paymentFile && !paymentProof.trim()) {
      alert('Please select a file or enter payment details');
      return;
    }
    
    setUploading(true);
    try {
      let proofData = paymentProof;
      
      // If file is selected, upload it first
      if (paymentFile) {
        const formData = new FormData();
        formData.append('file', paymentFile);
        formData.append('description', paymentProof);
        
        // Upload file and get URL/reference
        const baseURL = process.env.REACT_APP_API_URL || 'https://api.intracapitals.com';
        const uploadResponse = await fetch(`${baseURL}/api/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: formData,
        });
        
        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          proofData = `${paymentProof}\n\nFile: ${uploadData.file_url || uploadData.filename}`;
        }
      }
      
      // API call to upload payment proof
      await P2PMarketplaceService.uploadPaymentProof(order.id, proofData);
      setOrder(prev => ({ ...prev, status: 'payment_confirmed' }));
      setPaymentProof('');
      setPaymentFile(null);
      alert('Payment proof uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload payment proof');
    } finally {
      setUploading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    try {
      const response = await P2PMarketplaceService.sendChatMessage(orderId, newMessage);
      if (response.success) {
        // Add the message to local state immediately for better UX
        const message = {
          id: response.message.id,
          message: newMessage,
          sender_id: response.message.sender_id,
          created_at: new Date(),
          is_from_user: true
        };
        setChatMessages(prev => [...prev, message]);
        setNewMessage('');
        
        // Refresh chat messages to get any new ones
        setTimeout(() => {
          fetchChatMessages();
        }, 1000);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  const fetchChatMessages = async () => {
    try {
      const response = await P2PMarketplaceService.getChatMessages(orderId);
      if (response.success) {
        setChatMessages(response.messages || []);
      }
    } catch (error) {
      console.error('Failed to fetch chat messages:', error);
    }
  };

  const handleReleaseCoins = async () => {
    setReleasingCoins(true);
    try {
      const response = await P2PMarketplaceService.releaseCoins(orderId);
      if (response.success) {
        // Update the order status to completed
        setOrder(prev => ({ ...prev, status: 'completed' }));
        alert('Coins released successfully! Trade completed.');
      } else {
        alert(response.message || 'Failed to release coins. Please try again.');
      }
    } catch (error) {
      console.error('Failed to release coins:', error);
      alert('Failed to release coins. Please try again.');
    } finally {
      setReleasingCoins(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2, textAlign: 'center' }}>
          Loading transaction details...
        </Typography>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">Transaction not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => navigate('/p2p')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" fontWeight="bold">
            {order.type === 'buy' ? 'Buy' : 'Sell'} {order.cryptocurrency}
          </Typography>
        </Box>

        {/* Status Update Notification */}
        {statusUpdated && (
          <Alert 
            severity="success" 
            sx={{ mb: 3, animation: 'fadeIn 0.3s ease-in' }}
            onClose={() => setStatusUpdated(false)}
          >
            <Typography variant="body2">
              ✓ Order status updated! The page has been refreshed automatically.
            </Typography>
          </Alert>
        )}

        {/* Transaction Info Header */}
        <Card sx={{ mb: 3, backgroundColor: isDarkMode ? 'background.paper' : 'background.paper' }}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">Price</Typography>
                <Typography variant="h6" fontWeight="bold" color="success.main">
                  {order.rate.toLocaleString()} {order.fiatCurrency}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {timeLeft > 0 ? formatTime(timeLeft) : 'Expired'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">Quantity</Typography>
                <Typography variant="h6" fontWeight="bold">
                  {order.crypto_amount} {order.cryptocurrency}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">Payment Method</Typography>
                <Typography variant="h6" fontWeight="bold">
                  {order.payment_method}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">Payment Duration</Typography>
                <Typography variant="h6" fontWeight="bold">
                  {order.time_limit || 30}Min(s)
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Order Status */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Order Status
                </Typography>
                <Chip 
                  label={order.status?.replace('_', ' ').toUpperCase()} 
                  color={order.status === 'completed' ? 'success' : order.status === 'cancelled' ? 'error' : 'primary'}
                  sx={{ fontWeight: 'bold' }}
                />
              </Box>
              {order.status === 'completed' && (
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="success.main" fontWeight="bold">
                    ✓ Crypto Released
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {order.crypto_amount} {order.cryptocurrency} credited to your account
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          {/* Left Side - Transaction Flow */}
          <Grid item xs={12} md={8}>
            {/* Order Form */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold">
                    {order.type === 'buy' ? 'With Fiat' : 'With Crypto'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {order.type === 'buy' ? 'With Crypto' : 'With Fiat'}
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  label="Please enter amount"
                  value={order.fiat_amount?.toLocaleString() || '0'}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">{order.fiat_currency}</Typography>
                        <Button variant="outlined" size="small">All</Button>
                      </Box>
                    )
                  }}
                  sx={{ mb: 2 }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">I will receive</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {order.crypto_amount} {order.cryptocurrency}
                  </Typography>
                </Box>

                {order.status === 'payment_pending' && (
                  <>
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      sx={{
                        backgroundColor: order.type === 'buy' ? '#02C076' : '#F84960',
                        color: 'white',
                        fontWeight: 'bold',
                        py: 2,
                        mb: 2,
                        '&:hover': {
                          backgroundColor: order.type === 'buy' ? '#018A5B' : '#E03852'
                        }
                      }}
                    >
                      {order.type === 'buy' ? 'Buy' : 'Sell'}
                    </Button>

                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        {order.type === 'buy' 
                          ? 'If there is risk, the withdrawal may be delayed by up to 24 hours'
                          : 'Please wait for the counterparty to make payment. The tokens for this sale will be transferred out of your Funding Account.'
                        }
                      </Typography>
                    </Alert>
                  </>
                )}

                {/* User Payment Information - For Sell Orders */}
                {order.type === 'sell' && order.payment_details && Object.keys(order.payment_details).length > 0 && (
                  <Card sx={{ mb: 3, border: '2px solid', borderColor: 'primary.main' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                        Your Payment Information
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        The seller will send payment to your account using these details:
                      </Typography>
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {Object.entries(order.payment_details).map(([key, value]) => (
                          value && (
                            <Box key={key} sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              p: 2,
                              bgcolor: 'background.default',
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: 'divider'
                            }}>
                              <Typography variant="body2" color="text.secondary" sx={{ 
                                textTransform: 'capitalize', 
                                fontWeight: 'bold',
                                minWidth: '140px'
                              }}>
                                {key.replace(/_/g, ' ')}:
                              </Typography>
                              <Typography variant="body2" sx={{ 
                                fontWeight: 'medium', 
                                textAlign: 'right',
                                color: 'text.primary',
                                wordBreak: 'break-all'
                              }}>
                                {value}
                              </Typography>
                            </Box>
                          )
                        ))}
                      </Box>
                      
                      <Alert severity="success" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          ✓ Your payment details are ready. The seller will use this information to send you the payment.
                        </Typography>
                      </Alert>
                    </CardContent>
                  </Card>
                )}

                {/* Payment proof moved to chat as image upload */}
                {order.status === 'payment_pending' && (
                  <Box sx={{ mt: 3 }}>
                    <Button variant="outlined" fullWidth startIcon={<Chat />} onClick={() => setChatOpen(true)}>
                      Open Chat
                    </Button>
                  </Box>
                )}

                {/* Release Coin Button - Shows when partner has paid (only for sell orders) */}
                {order.status === 'paid' && order.type === 'sell' && (
                  <Box sx={{ mt: 3 }}>
                    <Alert severity="success" sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight="bold">
                        ✓ Payment Received! The partner has sent the payment to your account.
                      </Typography>
                      <Typography variant="body2">
                        Please verify the payment in your account and click "Release Coin" to complete the trade.
                      </Typography>
                    </Alert>
                    <Button 
                      variant="contained" 
                      fullWidth 
                      size="large"
                      startIcon={<CheckCircle />}
                      onClick={handleReleaseCoins}
                      disabled={releasingCoins}
                      sx={{
                        backgroundColor: '#02C076',
                        color: 'white',
                        fontWeight: 'bold',
                        py: 2,
                        '&:hover': {
                          backgroundColor: '#018A5B'
                        }
                      }}
                    >
                      {releasingCoins ? 'Releasing Coins...' : 'Release Coin'}
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Payment Details - For Buy Orders */}
            {order.type === 'buy' && order.payment_details && (
              <Card sx={{ mb: 3, border: '2px solid', borderColor: 'primary.main' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                    Payment Details
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Send your payment to the seller using these details:
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {Object.entries(order.payment_details).map(([key, value]) => {
                      // Handle nested objects (like payment method objects)
                      if (value && typeof value === 'object' && !Array.isArray(value)) {
                        return Object.entries(value).map(([nestedKey, nestedValue]) => (
                          nestedValue && (
                            <Box key={`${key}_${nestedKey}`} sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              p: 2,
                              bgcolor: 'background.default',
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: 'divider'
                            }}>
                              <Typography variant="body2" color="text.secondary" sx={{ 
                                textTransform: 'capitalize', 
                                fontWeight: 'bold',
                                minWidth: '140px'
                              }}>
                                {nestedKey.replace(/_/g, ' ')}:
                              </Typography>
                              <Typography variant="body2" sx={{ 
                                fontWeight: 'medium', 
                                textAlign: 'right',
                                color: 'text.primary',
                                wordBreak: 'break-all'
                              }}>
                                {nestedValue}
                              </Typography>
                            </Box>
                          )
                        ));
                      }
                      
                      // Handle simple key-value pairs
                      return value && typeof value === 'string' && (
                        <Box key={key} sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          p: 2,
                          bgcolor: 'background.default',
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider'
                        }}>
                          <Typography variant="body2" color="text.secondary" sx={{ 
                            textTransform: 'capitalize', 
                            fontWeight: 'bold',
                            minWidth: '140px'
                          }}>
                            {key.replace(/_/g, ' ')}:
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            fontWeight: 'medium', 
                            textAlign: 'right',
                            color: 'text.primary',
                            wordBreak: 'break-all'
                          }}>
                            {value}
                          </Typography>
                        </Box>
                      );
                    }).flat()}
                  </Box>
                  
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      💡 Make sure to send the exact amount and include the order reference when making payment.
                    </Typography>
                  </Alert>
                </CardContent>
              </Card>
            )}


            {/* Advertiser Terms */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Advertiser Terms
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                  {order.terms || 'No specific terms provided.'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Side - Partner Info & Chat */}
          <Grid item xs={12} md={4}>
            {/* Partner Info */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Transaction Info
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Avatar sx={{ width: 48, height: 48, backgroundColor: 'primary.main' }}>
                    {order.partner?.name?.charAt(0) || 'P'}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      {order.partner?.name || 'Partner'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      1h ago
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Good Rating %</Typography>
                    <Typography variant="h6" fontWeight="bold">{order.partner?.rating || 0}%</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Completed Order(s) in 30 Days</Typography>
                    <Typography variant="h6" fontWeight="bold">{order.partner?.total_trades || 0} Order(s)</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">30-Day Order Completion Rate (%)</Typography>
                    <Typography variant="h6" fontWeight="bold">{order.partner?.completion_rate || 0}%</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Avg. Release Time</Typography>
                    <Typography variant="h6" fontWeight="bold">{order.partner?.avg_release_time || 0} Minute(s)</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Replace inline chat with modal trigger */}
            <Card>
              <CardContent>
                <Button fullWidth variant="contained" startIcon={<Chat />} onClick={() => setChatOpen(true)}>
                  Open Chat
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </motion.div>
      <P2PChatDialog open={chatOpen} onClose={() => setChatOpen(false)} order={{ order_id: orderId, customer: { name: order?.partner?.name }, status: order?.status }} />
    </Container>
  );
};

export default P2PTransactionDetails;
